import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Trash2 } from "lucide-react"
import { type FormEvent, useEffect, useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Categoria, RolResumen } from "./tipos"

/**
 * Las categorías del cofre: cómo se llaman y quién las abre. Sólo con
 * `credencial:administrar`.
 *
 * ⚠ **Sin roles, la categoría la ve sólo quien administra.** Aquí no hay herencia como
 * en el Drive: vaciar la lista CIERRA. La pantalla lo dice en vez de enseñar una lista
 * en blanco, que se leería como «abierta a todos».
 *
 * ⚠ **Se guarda la lista COMPLETA de roles**, igual que la matriz de accesos: con dos
 * pestañas abiertas, mandar la diferencia la aplicaría sobre un estado que ya cambió.
 *
 * ⚠ **Renombrar no rompe nada.** Las fichas cuelgan del identificador, no del nombre, y
 * los accesos tampoco lo miran; el nombre es sólo para leerlo. Por eso se puede
 * corregir «Bancos» a «Bancos nacionales» sin tocar ni una credencial.
 *
 * ⚠ **El nombre y los accesos se guardan con un solo botón, y en ese orden.** Si el
 * nombre choca con otra categoría el servidor responde 409 y los accesos NO se
 * guardan: media cosa guardada es peor que ninguna, porque nadie sabe cuál quedó.
 */
export function CategoriasCofre({
  abierto,
  categorias,
  onCerrar,
}: {
  abierto: boolean
  categorias: Categoria[]
  onCerrar: () => void
}) {
  const id = useId()
  const clienteQuery = useQueryClient()
  const [elegidaId, setElegidaId] = useState<number | null>(null)
  const [marcados, setMarcados] = useState<Set<number>>(new Set())
  const [nombre, setNombre] = useState("")
  const [descripcion, setDescripcion] = useState("")
  const [nuevoNombre, setNuevoNombre] = useState("")
  const [nuevaDescripcion, setNuevaDescripcion] = useState("")

  const roles = useQuery({
    queryKey: ["credenciales", "roles"],
    queryFn: () => api.get<RolResumen[]>("/api/v1/credenciales/categorias/roles"),
    enabled: abierto,
  })

  const elegida = categorias.find((c) => c.id === elegidaId) ?? categorias[0]
  const idElegida = elegida?.id ?? null

  // ⚠ **Sólo al CAMBIAR de categoría, no cada vez que vuelve la lista del servidor.**
  // Con la categoría entera en las dependencias, cualquier refresco —el de guardar, o
  // el de otra pestaña— pisaría el nombre que se está escribiendo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ver la nota de arriba
  useEffect(() => {
    const actual = categorias.find((c) => c.id === idElegida)
    if (actual === undefined) return
    setNombre(actual.nombre)
    setDescripcion(actual.descripcion ?? "")
    setMarcados(new Set(actual.roles))
  }, [idElegida])

  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["credenciales"] })

  const crear = useMutation({
    mutationFn: () =>
      api.post<Categoria>("/api/v1/credenciales/categorias", {
        nombre: nuevoNombre,
        descripcion: nuevaDescripcion.trim() || null,
      }),
    onSuccess: (creada) => {
      setNuevoNombre("")
      setNuevaDescripcion("")
      setElegidaId(creada.id)
      void refrescar()
    },
  })

  const renombrar = useMutation({
    mutationFn: (categoriaId: number) =>
      api.patch<Categoria>(`/api/v1/credenciales/categorias/${categoriaId}`, {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
      }),
    onSuccess: () => void refrescar(),
  })

  const fijar = useMutation({
    mutationFn: (categoriaId: number) =>
      api.put<Categoria>(`/api/v1/credenciales/categorias/${categoriaId}/roles`, {
        roles: [...marcados],
      }),
    onSuccess: () => void refrescar(),
  })

  const borrar = useMutation({
    mutationFn: (categoriaId: number) =>
      api.delete(`/api/v1/credenciales/categorias/${categoriaId}`),
    onSuccess: () => {
      setElegidaId(null)
      void refrescar()
    },
  })

  function alternar(rolId: number) {
    const siguiente = new Set(marcados)
    if (siguiente.has(rolId)) siguiente.delete(rolId)
    else siguiente.add(rolId)
    setMarcados(siguiente)
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (nuevoNombre.trim() !== "") crear.mutate()
  }

  async function guardar(categoria: Categoria) {
    try {
      if (datosSucios) await renombrar.mutateAsync(categoria.id)
      if (rolesSucios) await fijar.mutateAsync(categoria.id)
    } catch {
      // El error queda en la mutación y se pinta abajo. Aquí sólo se corta la cadena:
      // ver la nota de la cabecera sobre no guardar media cosa.
    }
  }

  const datosSucios =
    elegida !== undefined &&
    (nombre.trim() !== elegida.nombre || descripcion.trim() !== (elegida.descripcion ?? ""))
  const rolesSucios =
    elegida !== undefined &&
    (elegida.roles.length !== marcados.size || elegida.roles.some((r) => !marcados.has(r)))
  const guardando = renombrar.isPending || fijar.isPending
  const error = crear.error ?? renombrar.error ?? fijar.error ?? borrar.error

  return (
    <Modal
      abierto={abierto}
      titulo="Categorías y accesos"
      descripcion="Cada categoría dice qué roles la abren. Sin roles, sólo la ve quien administra el cofre."
      onCerrar={onCerrar}
      ancho="lg"
    >
      <div className="grid gap-4 md:grid-cols-[15rem_1fr]">
        <div>
          {categorias.length > 0 && (
            <ul className="superficie overflow-hidden">
              {categorias.map((c, i) => (
                <li
                  key={c.id}
                  style={i === 0 ? undefined : { borderTop: "1px solid var(--cristal-filo)" }}
                >
                  <button
                    type="button"
                    onClick={() => setElegidaId(c.id)}
                    aria-current={c.id === elegida?.id}
                    className="block w-full px-3 py-2 text-left text-sm toque-cristal"
                    style={
                      c.id === elegida?.id
                        ? { backgroundColor: "var(--primary-suave)", color: "var(--primary-texto)" }
                        : undefined
                    }
                  >
                    <span className="block font-medium">{c.nombre}</span>
                    <span
                      className="block text-xs tabular-nums"
                      style={{ color: "var(--fg-muted)" }}
                    >
                      {c.credenciales} credencial{c.credenciales === 1 ? "" : "es"} ·{" "}
                      {c.roles.length === 0
                        ? "sólo administración"
                        : `${c.roles.length} rol${c.roles.length === 1 ? "" : "es"}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={enviar} className="mt-3 grid gap-2" aria-label="Nueva categoría">
            <label htmlFor={`${id}-nueva`} className="text-xs font-medium uppercase">
              Nueva categoría
            </label>
            <input
              id={`${id}-nueva`}
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              maxLength={80}
              placeholder="Bancos, SENIAT…"
              className="campo"
            />
            <input
              aria-label="Para qué es la nueva categoría"
              value={nuevaDescripcion}
              onChange={(e) => setNuevaDescripcion(e.target.value)}
              maxLength={200}
              placeholder="Para qué es (opcional)"
              className="campo"
            />
            <Boton type="submit" disabled={nuevoNombre.trim() === "" || crear.isPending}>
              <Plus size={16} aria-hidden={true} />
              Crear
            </Boton>
          </form>
        </div>

        <div>
          {elegida === undefined ? (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Todavía no hay categorías. Crea la primera a la izquierda.
            </p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor={`${id}-nombre`}
                    className="mb-1 block text-xs font-medium uppercase"
                  >
                    Nombre
                  </label>
                  <input
                    id={`${id}-nombre`}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    maxLength={80}
                    className="campo"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${id}-descripcion`}
                    className="mb-1 block text-xs font-medium uppercase"
                  >
                    Para qué es
                  </label>
                  <input
                    id={`${id}-descripcion`}
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    maxLength={200}
                    placeholder="Opcional"
                    className="campo"
                  />
                </div>
              </div>

              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                Cambiarle el nombre no mueve ninguna credencial: cuelgan de la categoría, no de cómo
                se llame.
              </p>

              <fieldset className="mt-3">
                <legend className="mb-2 text-xs font-medium uppercase">Roles que la abren</legend>
                {roles.isPending ? (
                  <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                    Cargando roles…
                  </p>
                ) : roles.isError ? (
                  <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
                    No se pudieron cargar los roles
                  </p>
                ) : (
                  <div className="grid gap-1.5 sm:grid-cols-2">
                    {roles.data.map((r) => (
                      <label key={r.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={marcados.has(r.id)}
                          onChange={() => alternar(r.id)}
                        />
                        {r.nombre}
                      </label>
                    ))}
                  </div>
                )}
                {marcados.size === 0 && (
                  <p className="mt-2 text-xs" style={{ color: "var(--warning)" }}>
                    Sin roles: sólo la verá quien administra el cofre.
                  </p>
                )}
              </fieldset>

              {error && (
                <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
                  {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
                </p>
              )}

              <div className="mt-4 flex flex-wrap justify-end gap-2">
                {elegida.credenciales === 0 && (
                  <Boton
                    variante="fantasma"
                    onClick={() => borrar.mutate(elegida.id)}
                    disabled={borrar.isPending}
                    style={{ color: "var(--destructive)" }}
                  >
                    <Trash2 size={16} aria-hidden={true} />
                    Borrar categoría
                  </Boton>
                )}
                <Boton
                  variante="primario"
                  onClick={() => void guardar(elegida)}
                  disabled={(!datosSucios && !rolesSucios) || nombre.trim() === "" || guardando}
                >
                  {guardando ? "Guardando…" : "Guardar cambios"}
                </Boton>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
